import { INTERCOM_APP_ID } from "@/config/constants";
import { LocalStorageKeys } from "@/config/localStorageKeys";
// eslint-disable-next-line no-restricted-imports
import { IntercomProvider } from "react-use-intercom";
import { ProfileType } from "../types";
import { useEffect, useRef } from "react";
import useIntercom from "@/hooks/useIntercom";
import { getToken } from "@/security/authentication";
import { useSession } from "@/security/RouteGuards";
import {
  getFromStorage,
  removeFromStorage,
  setToStorage,
} from "@/utils/localStorageUtils";

/**
 * Who the session belongs to: a tracking id once the profile is here, `null`
 * when nobody is signed in, and `undefined` while a signed-in profile is still
 * loading, when nothing can be decided yet.
 */
function sessionIdentity(profile?: ProfileType): string | null | undefined {
  if (profile) return profile.tracking_id;
  return getToken() ? undefined : null;
}

/**
 * Intercom keeps the conversations of whoever it was last identified as in a
 * cookie that outlives the page, and `update` with another person's details
 * does not clear them: only `shutdown` does. So the identity Intercom runs as
 * is remembered across page loads, and whenever the session's identity turns
 * out to be a different person, or nobody, Intercom is shut down and booted
 * again before it is told who is here. Federated access is where this showed:
 * released, the admin kept the hijacked user's conversation (R-1873). Logout
 * and account deletion end the session the same way, in the page they run in:
 * the widget stays, and is booted again for nobody, then identified once the
 * next person signs in, with no reload in between.
 *
 * While a signed-in profile is still loading nothing is booted at all: booted
 * with nobody named, Intercom resumes whoever its cookie holds, so a page
 * opened in that moment, the marketing site's included, would still be theirs.
 */
function EnsureIntercomBootedAndSynced({
  children,
  profile,
}: {
  children: React.ReactNode;
  profile?: ProfileType;
}) {
  const { boot, update, shutdown } = useIntercom();
  const isIntercomBooted = useRef(false);

  useEffect(() => {
    const identity = sessionIdentity(profile);
    if (identity === undefined) return;

    const props = profile
      ? {
          userId: profile.tracking_id,
          userHash: profile.intercom_signature,
          email: profile.email,
          name: `${profile.first_name} ${profile.last_name}`,
          hideDefaultLauncher: true,
        }
      : {
          hideDefaultLauncher: true,
        };

    if (!isIntercomBooted.current) {
      boot(props);
      isIntercomBooted.current = true;
    }

    const previousIdentity = getFromStorage(LocalStorageKeys.IntercomIdentity);
    if (previousIdentity && previousIdentity !== identity) {
      shutdown();
      boot(props);
    } else {
      // Intercom advises to call update immediately after boot to ensure user data is synced
      update(props);
    }

    if (identity) {
      setToStorage(LocalStorageKeys.IntercomIdentity, identity);
    } else {
      removeFromStorage(LocalStorageKeys.IntercomIdentity);
    }
  }, [boot, update, shutdown, profile]);

  return children;
}

/**
 * Mounted by `RouteGuard`, inside the session it establishes, so it renders
 * again whenever the session changes hands in place: a logout and the next
 * sign-in on the same page reach the effect above with no reload between.
 */
export default function IntercomProviderInternal({
  children,
}: {
  children: React.ReactNode;
}) {
  const { profile } = useSession();

  return (
    <IntercomProvider appId={INTERCOM_APP_ID}>
      <EnsureIntercomBootedAndSynced profile={profile}>
        {children}
      </EnsureIntercomBootedAndSynced>
    </IntercomProvider>
  );
}
